import React, { useEffect, useRef } from "react";
import { getVerificationData, getVerificationResult } from "../../api/aiprise";
import { useMutation, useQuery } from "@apollo/client";
import {
  UPDATE_KYC,
  UPDATE_USER,
  UPDATE_USER_NAMES,
} from "../../apollo/operations";
import { showToast } from "../../utils/showToast";
import { rentalAbi, rentalContract } from "../../abi/rental";
import { useWeb3Modal } from "@web3modal/wagmi/react";
import Loading from "../loading/Loading";

type IDVerificationProp = {
  userID: string;
  refetch?: any;
  // setShowConnectModal: React.Dispatch<React.SetStateAction<boolean>>;
  // showConnectModal: boolean;
  // setShowVerifyLoader: React.Dispatch<React.SetStateAction<boolean>>;
};

const IDVerification: React.FC<IDVerificationProp> = ({
  userID,
  refetch,
  // setShowConnectModal,
  // setShowVerifyLoader,
  // showConnectModal,
}) => {
  const verifyButtonRef = useRef<HTMLElement>(null);

  const [result, setResult] = React.useState<any>(null);

  const [verificationComplete, setVerificationComplete] = React.useState(false);

  const [showedToast, setShowedToast] = React.useState(false);

  const [updateKYC, { data: kycData, loading: kycLoading, error: kycError }] =
    useMutation(UPDATE_KYC, {
      onCompleted(data) {
        refetch();
        if (!showedToast) {
          setShowedToast(true);
        }
        showToast("ID Verification Successful", "success");
      },
      onError(error) {
        console.log(error);
        console.log(error);
        showToast(error.message, "error");
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          // console.log(error.graphQLErrors);

          const errorMessage =
            error.graphQLErrors[0]?.extensions?.originalError;
          console.error("GraphQL Error:", errorMessage);
        }
      },
    });

  const [updateUser, { data, loading, error }] = useMutation(UPDATE_USER, {
    onCompleted(data) {
      updateKYC({
        variables: {
          data: {
            userOnboardingStatus: "VerifyDriverLicense",
          },
          where: {
            id: userID,
          },
        },
      });
    },
    onError(error) {
      console.log(error);
      console.log(error);
      console.log(error.message);
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        // console.log(error.graphQLErrors);

        const errorMessage = error.graphQLErrors[0]?.extensions?.originalError;
        console.error("GraphQL Error:", errorMessage);
      }
    },
  });

  useEffect(() => {
    if (verifyButtonRef.current) {
      // Started Event
      verifyButtonRef.current!.addEventListener(
        "aiprise:started",
        function (e) {
          console.log(
            "Session started with ID: " + e.detail.verification_session_id
          );
        }
      );

      // Abandoned Event
      verifyButtonRef.current!.addEventListener(
        "aiprise:abandoned",
        function (e) {
          console.log(
            "Session with ID " +
              e.detail.verification_session_id +
              " was abandoned."
          );
        }
      );

      // Successful Event
      verifyButtonRef.current!.addEventListener(
        "aiprise:successful",
        function (e) {
          console.log(
            "Session with ID " +
              e.detail.verification_session_id +
              " was successful."
          );

          (async () => {
            const results = await getVerificationResult(
              e.detail.verification_session_id
            );
            if (results && !result && !verificationComplete) {
              setResult(results);
              // console.log("results", results);
              // console.log("result", result);

              setVerificationComplete(true);

              return;
            }
          })();

          // (async () => {
          //   const data = await getVerificationData(
          //     e.detail.verification_session_id
          //   );
          //   console.log("data", data);
          // })();
        }
      );

      // Continue Event
      verifyButtonRef.current!.addEventListener(
        "aiprise:continue",
        function (e) {
          console.log(
            "Session with ID " +
              e.detail.verification_session_id +
              " was continued."
          );
        }
      );

      // Error Event
      verifyButtonRef.current!.addEventListener("aiprise:error", function () {
        console.log("Unable to start session. Check your console for errors.");
      });
    }
  }, []);

  useEffect(() => {
    if (result && result?.id_info) {
      updateUser({
        variables: {
          data: {
            firstName: result?.id_info?.first_name,
            lastName: result?.id_info?.last_name,
            userDriverKYC: {
              aiprise_summary: {
                verification_result:
                  result?.aiprise_summary.verification_result,
              },
              client_reference_id: result?.client_reference_id,
              created_at: result?.created_at,
              environment: result?.environment,
              face_liveness_info: {
                result: result?.face_liveness_info.result,
              },
              face_match_info: {
                result: result?.face_match_info.result,
              },
              id_info: {
                address: {
                  full_address: result?.id_info?.address.full_address,
                  parsed_address: {
                    address_city:
                      result?.id_info?.address.parsed_address.address_city,
                    address_country:
                      result?.id_info?.address.parsed_address.address_country,
                    address_state:
                      result?.id_info?.address.parsed_address.address_state,
                    address_street_1:
                      result?.id_info?.address.parsed_address.address_street_1,
                    address_street_2:
                      result?.id_info?.address.parsed_address.address_street_2,
                    address_zip_code:
                      result?.id_info?.address.parsed_address.address_zip_code,
                  },
                },
                birth_date: result?.id_info?.birth_date,
                first_name: result?.id_info?.first_name,
                full_name: result?.id_info?.full_name,
                gender: result?.id_info?.gender,
                id_expiry_date: result?.id_info?.id_expiry_date,
                id_issue_date: result?.id_info?.id_issue_date,
                id_number: result?.id_info?.id_number,
                id_type: result?.id_info?.id_type,
                issue_country: result?.id_info?.issue_country,
                issue_country_code: result?.id_info?.issue_country_code,
                last_name: result?.id_info?.last_name,
                result: result?.id_info?.result,
                status: result?.id_info?.status,
              },
              status: result?.status,
              template_id: result?.template_id,
              verification_session_id: result?.verification_session_id,
            },
          },
          where: {
            id: userID,
          },
        },
      });
    }
  }, [result]);

  // (async () => {
  //   const data = await getVerificationData(
  //     "f896913d-382c-42f7-948d-0c2bf97200ee"
  //   );
  //   console.log(data);
  // })();

  // useEffect(() => {
  //   setShowVerifyLoader(loading);
  // }, [loading]);

  useEffect(() => {
    if (verificationComplete) {
      // showToast("ID Verification Successful", "success");
      setVerificationComplete(false);
      return;
    }
  }, [verificationComplete]);

  if (loading || kycLoading) return <Loading loading={loading || kycLoading} />;

  return (
    <div>
      <aiprise-button
        ref={verifyButtonRef}
        template-id={process.env.REACT_APP_AIPRISE_SANDBOX_TEMPLATE_ID || ""}
        mode="SANDBOX"
        callback-url="http://yourcallbackurl.com"
        client-reference-id={userID}
        icon="https://res.cloudinary.com/dicsazvfz/image/upload/v1695721086/logo_2_jqzkji.png"
        title="Identity Verification"
        color="#215759"
        text-color="#ffffff"
        theme={JSON.stringify({
          background: "light",
          color_brand: "#215759",
          color_brand_overlay: "#ffffff",
          font_name: "Plus Jakarta Sans",
        })}
      />
    </div>
  );
};

export default IDVerification;
